import { Match } from '@adornis/bh-matching-matches/entities/match.js';
import { readEntitiesByIDs } from '@adornis/bh-util/server/entity-reads.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { Entity } from '@adornis/baseql/decorators.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { runInServerContext } from '@adornis/baseql/server/server.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { BuddyUser } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { getMenteeUserClass, getMentorUserClass, getUserClasses } from '@adornis/bh-matching-user/user-class.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { ExportDataView } from '@adornis/data-view/db/export-data-view.js';
import { MailTemplate } from '@adornis/mails/db/mail-template.js';
import type { BuildEmailOptions } from '@adornis/mails/db/templates.js';
import { html } from 'lit';
import { localAttachmentPath } from '../mails/attachment-local-path.js';
import { BuddyMailAttachment } from './attachment.js';

export enum MailTemplateType {
  MATCHING_MENTOR = 'Matching_Mentor',
  MATCHING_MENTEE = 'Matching_Mentee',
  CONTACT_REMINDER_MENTOR = 'Contact_Reminder_Mentor',
  CONTACT_REMINDER_MENTEE = 'Contact_Reminder_Mentee',
  REGISTRATION_MENTOR = 'Registration_Mentor',
  REGISTRATION_MENTEE = 'Registration_Mentee',
  FREE_FORM = 'Free_Form',
  FREE_FORM_MENTEE = 'Free_Form_Mentee',
  FREE_FORM_MENTOR = 'Free_Form_Mentor',
}

export enum MailTemplateVars {
  SALUTATION = 'salutation',
}

@Entity()
export class BuddyMailTemplate extends MailTemplate {
  static override _class = 'BuddyMailTemplate';

  static override buildMailOptions: Partial<BuildEmailOptions> = {
    title: 'Buchungsheld Benachrichtigungsmail',
    footer: html`
      <div class="section" style="margin-top: 20px; font-size: 0.8em;">
        <div style="width: 100%">
          <div><a style="text-decoration: none;" href=${A.absoluteUrl('/imprint')}>Impressum</a></div>
          <div><a style="text-decoration: none;" href=${A.absoluteUrl('/data-privacy')}>Datenschutz</a></div>
        </div>
      </div>
    `,
    colorConfig: {
      mainBorder: 'none',
      mainBack: DesignSystem.currentTheme.getValue().colors.neutral.shade(50),
      wrapperBack: 'transparent',
    },
  };

  protected ensureAndGetMailTemplates() {}
}

export function convertUserToMailVarObject(user: BuddyUser, prefix?: string) {
  const prefixed = prefix ? `${prefix}.` : '';

  const vars = Object.fromEntries([
    ...Object.entries(user.toJSON()).map(([key, value]) => {
      const _value =
        (() => {
          if (Array.isArray(value)) return value.map(v => optionallyTranslateEnus(v as any)).join(', ');
          return optionallyTranslateEnus(value as any);
        })() || '';
      return [`${prefixed}${key}`, _value];
    }),

    ...(prefix === 'mentor'
      ? getMentorUserClass().getMailVariables(user)
      : getMenteeUserClass().getMailVariables(user)
    ).map(([key, value]) => [`${prefixed}${key}`, optionallyTranslateEnus(value)]),
  ]);

  const u = user as any;
  const title = u.title ? `${u.title} ` : '';
  vars[`${prefixed}fullName`] = `${title}${u.firstName} ${u.lastName}`;

  return vars;
}

function optionallyTranslateEnus(value: string) {
  //in hs-fulda, dictionary.enums didn't contain the values we tried to access. In that case we return the value itself
  return (ExportDataView._translator as any)?._options?.dictionary?.enums?.[value]?.[0] ?? value;
}

export const getMentorFields = () => {
  const filter = (x: string) =>
    !x.startsWith('_') &&
    !['ios_sub', 'username', 'roles', 'isOnline', 'createdAt', 'emailConfirmed', 'timeframeID', 'matches'].includes(x);

  return ['salutation', ...Object.keys(getMentorUserClass().fields).filter(filter)];
};

export const getMenteeFields = () => {
  const filter = (x: string) =>
    !x.startsWith('_') &&
    !['ios_sub', 'username', 'roles', 'isOnline', 'createdAt', 'emailConfirmed', 'timeframeID', 'matches'].includes(x);

  return ['salutation', ...Object.keys(getMenteeUserClass().fields).filter(filter)];
};

export const getMentorAndMenteeMailVariables = () => {
  return [
    `mentor.${MailTemplateVars.SALUTATION}`,
    ...getMentorFields().map(fieldKey => `mentor.${fieldKey}`),
    `mentee.${MailTemplateVars.SALUTATION}`,
    ...getMenteeFields().map(fieldKey => `mentee.${fieldKey}`),
  ];
};

export async function getMentorAndMenteeMailVariableData(matchID: string, emailType: MailTemplateType) {
  return runInServerContext(async () => {
    const matchFields = { _id: 1, mentorID: 1, menteeID: 1 } as const;
    const [match] = await readEntitiesByIDs<typeof Match, typeof matchFields>(Match, [matchID], matchFields);
    if (!match) throw new Error('No match found for ID ' + matchID);

    const userClasses = getUserClasses();
    if (!match.mentorID || !match.menteeID)
      throw new Error('match IDs missing, this state should not be allowed to exist at this point');
    const [mentor] = await readEntitiesByIDs(
      userClasses.mentor,
      [match.mentorID],
      selectionSet(() => userClasses.mentor, 2),
    );
    const [mentee] = await readEntitiesByIDs(
      userClasses.mentee,
      [match.menteeID],
      selectionSet(() => userClasses.mentee, 2),
    );

    if (!mentor || !mentee) {
      logger.error(
        { mentor: !!mentor, mentee: !!mentee, matchID: match._id },
        'Couldnt find mentor or mentee for match mail',
      );

      throw new Error('Couldnt find mentor or mentee for match mail');
    }

    const data = {
      ...convertUserToMailVarObject(mentor as any, 'mentor'),
      ...convertUserToMailVarObject(mentee as any, 'mentee'),
    };

    // attachments
    const mailTemplate = await loadMongoData(BuddyMailTemplate, { emailType }).then(res => res?.[0]);
    if (!mailTemplate) throw new Error('No mail template found for type ' + emailType);

    const attachments = {};
    const attachmentData = await readEntitiesByIDs(
      BuddyMailAttachment,
      mailTemplate.activatedAttachments,
      selectionSet(() => BuddyMailAttachment, 2),
    );
    for (const attachment of attachmentData) {
      attachments[attachment._id] = await localAttachmentPath(attachment);
    }

    return { data, attachments, scope: 'default' };
  });
}
